import { workingDaysInclusive } from './calendar';
import { maxDate, minDate } from './date';
/**
 * Computes the span of every group.
 *
 * `assignments` maps a task to the group it hangs in, or to null when it hangs
 * free. Tasks without dates are ignored rather than treated as zero, so an
 * uncalculated plan yields empty groups instead of wrong ones.
 */
export function rollUpGroups(groups, assignments, schedules, calendar) {
    const byId = new Map(groups.map((group) => [group.id, group]));
    const children = new Map();
    for (const group of groups) {
        if (group.parentId !== null && byId.has(group.parentId)) {
            const siblings = children.get(group.parentId) ?? [];
            siblings.push(group.id);
            children.set(group.parentId, siblings);
        }
    }
    const directTasks = new Map();
    for (const [taskId, groupId] of assignments) {
        if (groupId === null || !byId.has(groupId))
            continue;
        const list = directTasks.get(groupId) ?? [];
        list.push(taskId);
        directTasks.set(groupId, list);
    }
    const depthOf = (id) => {
        let depth = 0;
        let current = byId.get(id)?.parentId ?? null;
        let guard = 0;
        while (current !== null && guard++ < 1000) {
            depth++;
            current = byId.get(current)?.parentId ?? null;
        }
        return depth;
    };
    const result = new Map();
    // Post-order: a group is only computed once its children are, so the parent
    // simply merges their results instead of walking the subtree again.
    const visit = (id, guard) => {
        const existing = result.get(id);
        if (existing)
            return existing;
        if (guard > 1000) {
            // A cycle in the outline would otherwise recurse forever. The outline is
            // guarded against this when edited; this is the belt to that braces.
            const empty = {
                id,
                earlyStart: null,
                earlyFinish: null,
                durationDays: 0,
                critical: false,
                taskCount: 0,
                depth: depthOf(id),
            };
            result.set(id, empty);
            return empty;
        }
        let start = null;
        let finish = null;
        let critical = false;
        let taskCount = 0;
        for (const taskId of directTasks.get(id) ?? []) {
            const dates = schedules.get(taskId);
            if (!dates)
                continue;
            taskCount++;
            start = start === null ? dates.earlyStart : minDate(start, dates.earlyStart);
            finish = finish === null ? dates.earlyFinish : maxDate(finish, dates.earlyFinish);
            if (dates.critical)
                critical = true;
        }
        for (const childId of children.get(id) ?? []) {
            const child = visit(childId, guard + 1);
            taskCount += child.taskCount;
            if (child.critical)
                critical = true;
            if (child.earlyStart !== null) {
                start = start === null ? child.earlyStart : minDate(start, child.earlyStart);
            }
            if (child.earlyFinish !== null) {
                finish = finish === null ? child.earlyFinish : maxDate(finish, child.earlyFinish);
            }
        }
        const rollup = {
            id,
            earlyStart: start,
            earlyFinish: finish,
            durationDays: start !== null && finish !== null
                ? workingDaysInclusive(calendar, start, finish)
                : 0,
            critical,
            taskCount,
            depth: depthOf(id),
        };
        result.set(id, rollup);
        return rollup;
    };
    for (const group of groups)
        visit(group.id, 0);
    return result;
}
//# sourceMappingURL=rollup.js.map